"use client";

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useCheckoutBase, defaultGuest } from "./use-checkout-base";
import {
  resolveGuestCollectionMode,
  hasRenderableQuestions,
} from "../utils/guest-questions";
import { resolveSessionLocationName } from "../utils/session-location";
import { participantsToPayload } from "../participants-step";
import { buildTicketSeatGroups, totalSeatCount } from "../utils/ticket-seat-groups";
import { useV2Pricing } from "./use-v2-pricing";
import { effectivePreselectAvailability } from "./preselect-availability";
import { deriveV2SessionPrice, getLegacyManualPrice } from "../../../lib/pricing";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";
import { codeBasePence } from "@obby/lib";
import type {
  Session,
  TicketType,
  TicketSelection,
  CheckoutStep,
  Guest,
} from "../utils/types";

export type UseCheckoutTermProps = {
  course: any;
  session?: any;
  sessions?: any[];
  ticketTypes?: any[];
  ticketTypesV2?: any[];
  /** IDs of ticket types the current buyer has already purchased for this
   *  course — drives locked/hidden display state for prior-purchase-gated
   *  V2 tickets. Sourced from the same course payload as `ticketTypesV2`. */
  priorPurchasedTicketIds?: string[];
  /** Clamped paid-booking counts for the current buyer at this school —
   *  drives the customer-history pricing conditions (FIRST_TIME_BOOKING,
   *  RETURNING_CUSTOMER, BOOKING_COUNT_IN_PERIOD) in the price preview.
   *  Sourced from the same course payload as `ticketTypesV2`. */
  customerBookingHistory?: CustomerBookingHistory;
  enabledAddOns?: any[];
  addOnGroups?: any[];
  locations?: any[]; // For location name resolution
  user?: any;
  bundle?: any;
  bundleID?: string;
  isSendingMaterial?: boolean;
  /** Pre-select a session by ID (skips date selection step when session is found) */
  initialSessionId?: string;
  /** Seats reserved for this buyer per session (membership allocation) — added
   * to general availability when deciding sold-out, mirroring DateSelectionStep. */
  reservedSessionQuantities?: Record<string, number>;
  /**
   * Whether the listing allows the buyer to skip per-guest details.
   * Frozen and no longer consulted — buyer details are always collected
   * downstream in the basket checkout.
   */
  allowPrefillOthersGuestsOnCheckout?: boolean;
  /** Whether the school collects attendees via the participants step. */
  participantsEnabled?: boolean;
  /** Whether the school has any checkout-eligible participant fields. */
  hasParticipantFields?: boolean;
  onLoadMoreDates: (args: { page: number; limit: number; sessionId?: string }) => Promise<any>;
  onValidateDiscount?: (code: string, email: string) => Promise<any>;
  onStepChange?: () => void;
};

export function useCheckoutTerm({
  course,
  session,
  sessions: initialSessions,
  ticketTypes: initialTicketTypes,
  ticketTypesV2 = [],
  priorPurchasedTicketIds,
  customerBookingHistory,
  enabledAddOns = [],
  addOnGroups = [],
  locations = [],
  user,
  bundle,
  bundleID,
  isSendingMaterial = false,
  initialSessionId,
  reservedSessionQuantities,
  participantsEnabled = false,
  hasParticipantFields = false,
  onLoadMoreDates,
  onValidateDiscount,
  onStepChange,
}: UseCheckoutTermProps) {
  const showParticipantsStep = participantsEnabled && hasParticipantFields;
  const isV2 = course?.ticketVersion === "v2";
  const page = useRef(initialSessions === undefined ? 0 : 1);
  // Store ticket types ref for access in callbacks
  const ticketTypesRef = useRef<any[]>(initialTicketTypes ?? []);

  // Base checkout functionality
  const base = useCheckoutBase({
    user,
    enabledAddOns,
    addOnGroups,
    isSendingMaterial,
    onStepChange,
  });

  // Location NAME for a session (shared resolver — utils/session-location.ts).
  // undefined → the session row renders no location pin.
  const getSessionLocation = useCallback(
    (s: any): string | undefined => resolveSessionLocationName(s, locations),
    [locations],
  );

  // V1 helper to calculate effective price based on typeCharge and session
  const calculateEffectivePrice = useCallback(
    (basePrice: number, ticketType: any, session: any): number => {
      const numberOfSessions = session?.numberOfSessions ?? (session?.additionalClasses?.length ?? 0) + 1;

      if (ticketType?.typeCharge === "perPersonPerLesson") {
        return Math.ceil(basePrice * numberOfSessions);
      } else if (ticketType?.typeCharge === "perPerson" && session?.oldNumberOfSessions) {
        const oldNumberOfSessions = session.oldNumberOfSessions;
        const currentNumber = session?.oldNumber ?? session?.number ?? 1;
        const fraction = (oldNumberOfSessions - currentNumber + 1) / oldNumberOfSessions;
        return Math.ceil(basePrice * fraction);
      }
      return basePrice;
    },
    [],
  );

  // Helper to get lowest price from ticket types for a session
  const getSessionPrice = useCallback(
    (s: any): number | undefined => {
      const legacyManualPrice = getLegacyManualPrice(course);
      if (legacyManualPrice !== undefined) {
        return legacyManualPrice;
      }

      const sessionTicketTypes = s.ticketTypes || [];
      const prices: number[] = [];
      const numberOfSessions =
        s?.numberOfSessions ?? (s?.additionalClasses?.length ?? 0) + 1;

      if (sessionTicketTypes.length > 0) {
        sessionTicketTypes.forEach((stt: any) => {
          if (stt.status !== "enabled") return;
          if (stt.maxLimit !== undefined && stt.maxLimit <= 0) return;

          const baseTicket = initialTicketTypes?.find(
            (tt: any) => tt._id === stt.ticketTypeId || tt._id === stt._id,
          );
          if (baseTicket?.price !== undefined) {
            const effectivePrice = calculateEffectivePrice(baseTicket.price, baseTicket, s);
            prices.push(effectivePrice);
            return;
          }

          // V1 ticketTypes has nothing for this id — try V2 (course.ticketVersion
          // === "v2" carries no legacy ticketTypes at all, so this is the only path).
          const v2Ticket = ticketTypesV2.find((tt: any) => tt.id === stt.ticketTypeId);
          if (v2Ticket) {
            prices.push(deriveV2SessionPrice(v2Ticket, numberOfSessions));
          }
        });
      } else if (initialTicketTypes && initialTicketTypes.length > 0) {
        initialTicketTypes.forEach((tt: any) => {
          if (tt.price !== undefined) {
            const effectivePrice = calculateEffectivePrice(tt.price, tt, s);
            prices.push(effectivePrice);
          }
        });
      } else if (ticketTypesV2.length > 0) {
        ticketTypesV2.forEach((tt: any) => {
          prices.push(deriveV2SessionPrice(tt, numberOfSessions));
        });
      }

      if (prices.length > 0) {
        return Math.min(...prices);
      }

      // Deprecated denormalised session.price: last resort only — it goes
      // stale when tickets are repriced, so it must never shadow a live
      // ticket-derived price above.
      if (s.price !== undefined) {
        return s.price;
      }

      return course?.price;
    },
    [
      course?.price,
      course?.isPriceFrom,
      course?.priceDisplayMode,
      initialTicketTypes,
      ticketTypesV2,
      calculateEffectivePrice,
    ],
  );

  // Map sessions
  const mapSession = useCallback(
    (s: any): Session => {
      return {
        id: s._id,
        date: s.startDateTime,
        title: s.title,
        description: s.description,
        image: s.image,
        startDateTime: s.startDateTime,
        duration: s.duration,
        availability: s.availability,
        isOnline: s.isOnline ?? course?.isOnline,
        isTerm: s.isTerm,
        location: getSessionLocation(s),
        termName: s.termName,
        termType: s.termType,
        price: getSessionPrice(s),
        additionalClasses: s.additionalClasses,
        numberOfSessions:
          s.numberOfSessions ?? (s.additionalClasses?.length ?? 0) + 1,
        oldNumberOfSessions: s.oldNumberOfSessions,
        number: s.number ?? 1,
        oldNumber: s.oldNumber,
        tutorId: s.tutor,
        tutorName: s.tutorName || s.tutor?.name,
        tutorImage: s.tutorImage || s.tutor?.image?.url,
        ticketTypes: s.ticketTypes,
        eventId: s.eventId,
      } as Session;
    },
    [getSessionLocation, getSessionPrice, course?.isOnline],
  );

  // Sessions state
  const [sessions, setSessions] = useState<Session[]>(() =>
    (initialSessions ?? []).map(mapSession),
  );
  const [selectedSession, setSelectedSession] = useState<Session | null>(() =>
    session ? mapSession(session) : null,
  );
  const [isFullyLoaded, setIsFullyLoaded] = useState(
    initialSessions !== undefined && initialSessions.length < 15,
  );

  const hasAppliedInitialSessionRef = useRef(false);

  // Ticket selections
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>(
    [],
  );

  // --- V2 pricing (delegated to useV2Pricing) ---
  const sessionTicketTypesRaw = (selectedSession as any)?.ticketTypes ?? [];

  const v2 = useV2Pricing({
    enabled: isV2,
    ticketTypesV2,
    ticketSelections,
    selectedSession,
    rawSessionTicketTypes: sessionTicketTypesRaw,
    numberOfSessions: selectedSession?.numberOfSessions ?? 1,
    courseTimezone: course?.timezone ?? "Europe/London",
    discount: base.discount,
    addOnsTotal: base.addOnsTotal,
    bundleID,
    selectedSessions: selectedSession ? [{ date: selectedSession.startDateTime }] : undefined,
    priorPurchasedTicketIds,
    customerBookingHistory,
  });

  // --- V1 ticket types (only used when NOT V2) ---
  const v1TicketTypes = useMemo((): TicketType[] => {
    if (isV2) return [];

    const sessionTicketTypes = (selectedSession as any)?.ticketTypes || [];

    const filteredTicketTypes = (initialTicketTypes ?? [])
      .filter((tt: any) => {
        if (!tt || !tt._id) return false;
        if (!selectedSession || sessionTicketTypes.length === 0) return true;
        const sessionTicketType = sessionTicketTypes.find(
          (stt: any) => stt.ticketTypeId === tt._id,
        );
        return sessionTicketType && sessionTicketType.status === "enabled";
      })
      .map((tt: any) => {
        const sessionTicketType = sessionTicketTypes.find(
          (stt: any) => stt.ticketTypeId === tt._id,
        );

        let basePrice = tt.price;

        if (tt.pricesAvailable?.length && selectedSession?.startDateTime) {
          const sortedPrices = [...tt.pricesAvailable].sort(
            (a: any, b: any) =>
              new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime(),
          );
          const applicablePrice = sortedPrices.find(
            (p: any) =>
              new Date(p.fromDate) < new Date(selectedSession.startDateTime),
          );
          if (applicablePrice) {
            basePrice = applicablePrice.price;
          }
        }

        let effectivePrice = basePrice;
        if (selectedSession) {
          if (tt.typeCharge === "perPersonPerLesson") {
            const numberOfSessions = selectedSession.numberOfSessions ?? 1;
            effectivePrice = Math.ceil(basePrice * numberOfSessions);
          } else if (tt.typeCharge === "perPerson") {
            const oldNumberOfSessions =
              selectedSession.oldNumberOfSessions ?? 1;
            const currentNumber = selectedSession.oldNumber ?? selectedSession.number ?? 1;
            const fraction =
              (oldNumberOfSessions - currentNumber + 1) / oldNumberOfSessions;
            effectivePrice = Math.ceil(basePrice * fraction);
          }
        }

        let effectiveDepositAmount = tt.depositAmount;

        return {
          id: tt._id,
          name: tt.name,
          description: tt.description,
          price: tt.price,
          effectivePrice,
          maxQuantity: tt.maxQuantity ?? Infinity,
          minQuantity: tt.minQuantity ?? 0,
          soldOut: tt.soldOut,
          numberOfGuestsPerTicket: tt.numberOfGuestsPerTicket ?? 1,
          // Soft age range (booking seats Phase 5): warns, never blocks.
          minAge: tt.minAge ?? null,
          maxAge: tt.maxAge ?? null,
          maxLimit: sessionTicketType?.maxLimit ?? tt.maxLimit,
          currentSold: sessionTicketType?.currentSold ?? tt.currentSold ?? 0,
          isInTimeConstraint: tt.isInTimeConstraint,
          typeCharge: tt.typeCharge,
          ticketTypeGroup: tt.ticketTypeGroup,
          depositAmount: tt.depositAmount,
          effectiveDepositAmount,
          pricesAvailable: tt.pricesAvailable,
          bookingPolicyId: tt.bookingPolicyId ?? null,
          bookingPolicy: tt.bookingPolicy ?? null,
        };
      });

    return filteredTicketTypes;
  }, [initialTicketTypes, selectedSession, isV2]);

  // Unified ticket types: V2 hook or V1 computation
  const ticketTypes = isV2 ? v2.ticketTypes : v1TicketTypes;

  // V1 helper to get effective price based on session date and deposit status
  const getTicketPrice = useCallback(
    (ticketType: TicketType, useDeposit: boolean = false): number => {
      let effectivePrice = ticketType.price;

      if (
        ticketType.pricesAvailable?.length &&
        selectedSession?.startDateTime
      ) {
        const sortedPrices = [...ticketType.pricesAvailable].sort(
          (a, b) =>
            new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime(),
        );
        const applicablePrice = sortedPrices.find(
          (p) => new Date(p.fromDate) < new Date(selectedSession.startDateTime),
        );
        if (applicablePrice) {
          effectivePrice = applicablePrice.price;
        }
      }

      if (selectedSession) {
        if (ticketType.typeCharge === "perPersonPerLesson") {
          const numberOfSessions = selectedSession.numberOfSessions ?? 1;
          effectivePrice = Math.ceil(effectivePrice * numberOfSessions);
        } else if (ticketType.typeCharge === "perPerson") {
          const oldNumberOfSessions = selectedSession.oldNumberOfSessions ?? 1;
          const currentNumber = selectedSession.oldNumber ?? selectedSession.number ?? 1;
          const fraction =
            (oldNumberOfSessions - currentNumber + 1) / oldNumberOfSessions;
          effectivePrice = Math.ceil(effectivePrice * fraction);
        }
      }

      if (
        useDeposit &&
        ticketType.ticketTypeGroup === "deposit" &&
        ticketType.depositAmount
      ) {
        effectivePrice = ticketType.depositAmount;
      }

      return effectivePrice;
    },
    [selectedSession],
  );

  // Pre-fill personal details from bundle
  useEffect(() => {
    if (bundle) {
      const nameParts = bundle.buyerName?.split(" ") || [];
      const bundleGuest: Guest = {
        ...defaultGuest,
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: bundle.buyerEmail || "",
        phoneNumber: bundle.buyerPhone || "",
      };
      base.setPersonalDetails((prev) => ({
        ...prev,
        guests: [bundleGuest],
      }));
    }
  }, [bundle]);

  // Current step — start at tickets if a session (object) or session ID is
  // pre-selected. The session itself may not be loaded yet; we land on the
  // tickets step optimistically and the flow renders a loading state until
  // the matching session arrives via loadMoreDates().
  const [step, setStep] = useState<CheckoutStep>(() =>
    session || initialSessionId ? "tickets" : "dates",
  );

  // Pre-select session by ID when initialSessionId is provided and sessions are loaded
  useEffect(() => {
    if (
      !initialSessionId ||
      hasAppliedInitialSessionRef.current ||
      sessions.length === 0
    ) {
      return;
    }

    const matchingSession = sessions.find((s) => s.id === initialSessionId);
    const matchingEventSession = sessions.find((s: any) => s.eventId === initialSessionId);
    const preselected = matchingSession ?? matchingEventSession;

    if (preselected) {
      hasAppliedInitialSessionRef.current = true;
      // Anchor the deep-linked date either way, so the dates step highlights it.
      setSelectedSession(preselected);
      // A pre-selected session that's SOLD OUT for this buyer (e.g. a queue-held
      // term reached via a timetable "Book Now" deep link) must NOT skip the date
      // step's sold-out gate. Land on "dates", where DateSelectionStep renders it
      // disabled + "Join Waiting List", instead of walking the customer through a
      // doomed checkout. Uses EFFECTIVE availability (server availability minus
      // the buyer's own basket-held seats, matching the date card); a claimer's
      // ?offer= token keeps their held seats visible server-side, so they still
      // auto-advance to tickets.
      if (
        effectivePreselectAvailability(preselected, reservedSessionQuantities) <= 0
      ) {
        setStep("dates");
        return;
      }
      setStep("tickets");
    }
  }, [initialSessionId, sessions, reservedSessionQuantities]);

  // Fallback: if the initial load completed but the target session was never
  // matched (course/session linkage off, expired, etc.), drop back to the
  // date step so the user isn't stuck on an empty tickets view.
  useEffect(() => {
    if (
      !initialSessionId ||
      hasAppliedInitialSessionRef.current ||
      base.isLoading ||
      selectedSession
    ) {
      return;
    }
    if (sessions.length > 0 || isFullyLoaded) {
      hasAppliedInitialSessionRef.current = true;
      setStep("dates");
    }
  }, [initialSessionId, base.isLoading, sessions, isFullyLoaded, selectedSession]);

  // --- V1 total calculation ---
  const v1Total = useMemo(() => {
    if (isV2 || bundleID) return 0;

    const ticketsTotal = ticketSelections.reduce((sum, selection) => {
      const ticketType = v1TicketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      if (!ticketType) return sum;
      const isDeposit = ticketType.ticketTypeGroup === "deposit";
      const effectivePrice = getTicketPrice(ticketType, isDeposit);
      return sum + effectivePrice * selection.quantity;
    }, 0);

    const grossTotal = ticketsTotal + base.addOnsTotal;

    let disc = 0;
    if (base.discount) {
      const { value, type } = base.discount;
      // A code that excludes add-ons (DEV-327) is priced on the tickets only.
      const discBase = codeBasePence(base.discount, grossTotal, base.addOnsTotal);
      if (type === "PERCENTAGE") {
        disc = Math.round((discBase * Math.min(value, 100)) / 100);
      } else {
        disc = Math.min(discBase, value);
      }
    }

    return Math.max(0, grossTotal - disc);
  }, [
    isV2,
    ticketSelections,
    v1TicketTypes,
    base.addOnsTotal,
    base.discount,
    getTicketPrice,
    bundleID,
  ]);

  // Unified total
  const total = bundleID ? 0 : isV2 ? v2.total : v1Total;

  // V1 remaining balance for deposit tickets
  const v1RemainingBalance = useMemo(() => {
    if (isV2) return 0;
    return ticketSelections.reduce((sum, selection) => {
      const ticketType = v1TicketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      if (!ticketType) return sum;
      if (ticketType.ticketTypeGroup !== "deposit") return sum;

      const fullPrice = getTicketPrice(ticketType, false);
      const depositPrice = getTicketPrice(ticketType, true);
      return sum + (fullPrice - depositPrice) * selection.quantity;
    }, 0);
  }, [isV2, ticketSelections, v1TicketTypes, getTicketPrice]);

  const totalRemainingBalance = isV2
    ? v2.totalRemainingBalance
    : v1RemainingBalance;

  // V1 discount amount for display
  const v1DiscountAmount = useMemo(() => {
    if (isV2 || bundleID || !base.discount) return 0;

    const ticketsTotal = ticketSelections.reduce((sum, selection) => {
      const ticketType = v1TicketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      if (!ticketType) return sum;
      const isDeposit = ticketType.ticketTypeGroup === "deposit";
      const effectivePrice = getTicketPrice(ticketType, isDeposit);
      return sum + effectivePrice * selection.quantity;
    }, 0);

    const grossTotal = ticketsTotal + base.addOnsTotal;
    const { value, type } = base.discount;
    // A code that excludes add-ons (DEV-327) is priced on the tickets only.
    const discBase = codeBasePence(base.discount, grossTotal, base.addOnsTotal);

    if (type === "PERCENTAGE") {
      return Math.round((discBase * Math.min(value, 100)) / 100);
    }
    return Math.min(discBase, value);
  }, [
    isV2,
    ticketSelections,
    v1TicketTypes,
    base.addOnsTotal,
    base.discount,
    getTicketPrice,
    bundleID,
  ]);

  const discountAmount = isV2 ? v2.discountAmount : v1DiscountAmount;

  // Unified constraint errors
  const constraintErrors = v2.constraintErrors;

  // Check for mixed booking policies across selected tickets
  const hasMixedPolicies = useMemo(() => {
    const selectedTicketTypeIds = ticketSelections
      .filter((s) => s.quantity > 0)
      .map((s) => s.ticketTypeId);
    if (selectedTicketTypeIds.length <= 1) return false;

    const policyIds = selectedTicketTypeIds
      .map((id) => ticketTypes.find((tt) => tt.id === id)?.bookingPolicyId)
      .filter((id): id is string => id != null);

    if (policyIds.length <= 1) return false;
    return new Set(policyIds).size > 1;
  }, [ticketSelections, ticketTypes]);

  // Is free
  const isFree = useMemo(() => {
    if (bundleID) return true;
    return ticketSelections.some((s) => s.quantity > 0) && total === 0;
  }, [ticketSelections, total, bundleID]);

  // Remaining availability
  const remainingAvailability = useMemo(() => {
    if (!selectedSession) return 0;

    const baseAvailability = selectedSession.availability ?? 0;

    const selectedSeats = ticketSelections.reduce((sum, selection) => {
      const ticketType = ticketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      const guestsPerTicket = ticketType?.numberOfGuestsPerTicket ?? 1;
      return sum + selection.quantity * guestsPerTicket;
    }, 0);

    return Math.max(0, baseAvailability - selectedSeats);
  }, [selectedSession, ticketSelections, ticketTypes]);

  // Total guest count (seats) derived from current ticket selections.
  // Seats grouped by ticket, the one derivation every seat count reads from
  // (mirrors the server's computeReserveSeatCounts / BookingSeat.planSeats).
  const ticketSeatGroups = useMemo(
    () => buildTicketSeatGroups(ticketSelections, ticketTypes),
    [ticketSelections, ticketTypes],
  );
  const totalGuestCount = useMemo(() => totalSeatCount(ticketSeatGroups), [ticketSeatGroups]);

  // How this listing collects guests: additional guests come via the booking
  // form when it carries the (enabled) system identity questions; without
  // them, guest details simply aren't collected. The buyer is always
  // collected at the basket page, so there is no in-flow personal step.
  const { collectSystemGuests } = resolveGuestCollectionMode(
    course.userQuestionForm?.questions,
    {
      formEnabled: course.userQuestionForm?.isEnabled,
    },
  );

  // Visible steps for term checkout
  const visibleSteps = useMemo(() => {
    const steps: CheckoutStep[] = ["dates", "tickets"];

    if (enabledAddOns.length > 0 || addOnGroups.length > 0) {
      steps.push("addons");
    }

    // Participants ("Who is attending?") — collected before the booking-question
    // form. When enabled it replaces the system guest-identity questions.
    if (showParticipantsStep) {
      steps.push("participants");
    }

    // Show the booking-question step when the form is enabled and at least one
    // question actually renders for this booking. A form containing only system
    // identity questions is skipped when there are no additional guests (or when
    // participants own identity collection).
    if (
      course.userQuestionForm?.isEnabled &&
      hasRenderableQuestions(
        course.userQuestionForm?.questions,
        totalGuestCount,
        collectSystemGuests,
        showParticipantsStep,
      )
    ) {
      steps.push("userform");
    }

    steps.push("basketChoice");

    return steps;
  }, [
    enabledAddOns,
    addOnGroups,
    course.userQuestionForm,
    collectSystemGuests,
    totalGuestCount,
    showParticipantsStep,
  ]);

  // Step navigation
  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      const nextStep = visibleSteps[nextIndex]!;
      setStep(nextStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = visibleSteps[prevIndex]!;
      setStep(prevStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        setStep(targetStep);
        onStepChange?.();
      }
    },
    [visibleSteps, onStepChange],
  );

  // Session selection
  const selectSession = useCallback((session: Session) => {
    setSelectedSession(session);
  }, []);

  // Ticket selection
  const updateTicketSelection = useCallback(
    (ticketTypeId: string, quantity: number) => {
      setTicketSelections((prev) => {
        const existingIndex = prev.findIndex(
          (sel) => sel.ticketTypeId === ticketTypeId,
        );

        let newSelections: TicketSelection[];
        if (quantity === 0) {
          newSelections = prev.filter(
            (sel) => sel.ticketTypeId !== ticketTypeId,
          );
        } else if (existingIndex >= 0) {
          newSelections = [...prev];
          newSelections[existingIndex] = { ticketTypeId, quantity };
        } else {
          newSelections = [...prev, { ticketTypeId, quantity }];
        }

        const totalGuests = newSelections.reduce((sum, sel) => {
          const ticketType = ticketTypesRef.current.find(
            (tt: any) => tt._id === sel.ticketTypeId,
          );
          const guestsPerTicket = ticketType?.numberOfGuestsPerTicket ?? 1;
          return sum + (sel.quantity * guestsPerTicket);
        }, 0);
        base.setPersonalDetails((prev) => {
          const newGuests = [...prev.guests];
          while (newGuests.length < totalGuests) {
            newGuests.push({ ...defaultGuest, guest: true });
          }
          while (newGuests.length > totalGuests && newGuests.length > 1) {
            newGuests.pop();
          }
          return { ...prev, guests: newGuests };
        });

        return newSelections;
      });
    },
    [],
  );

  // Load more dates
  const loadMoreDates = useCallback(async () => {
    if (base.isLoading || isFullyLoaded) return;

    base.setIsLoading(true);
    try {
      const isFirstLoad = page.current === 0;
      const data = await onLoadMoreDates({
        page: ++page.current,
        limit: 15,
        sessionId: isFirstLoad ? initialSessionId : undefined,
      });
      const newSessions = (
        Array.isArray(data) ? data : (data.sessions ?? [])
      ).map(mapSession);

      setSessions((prev) => [...prev, ...newSessions]);
      setIsFullyLoaded(newSessions.length < 15);
    } catch (error) {
      setIsFullyLoaded(true);
    } finally {
      base.setIsLoading(false);
    }
  }, [base.isLoading, isFullyLoaded, onLoadMoreDates, mapSession, initialSessionId]);

  // Initial load
  useEffect(() => {
    if (initialSessions === undefined) {
      loadMoreDates();
    }
  }, []);

  // Apply discount
  const applyDiscount = useCallback(
    async (code: string) => {
      if (!onValidateDiscount) return;

      const email = base.personalDetails.guests[0]?.email || "";
      if (!email) {
        base.setError("Please enter email first");
        return;
      }

      base.setIsLoading(true);
      base.clearError();

      try {
        const discountResult = await onValidateDiscount(code, email);

        if (discountResult && discountResult.value !== undefined) {
          base.setDiscount({
            value: discountResult.value,
            type: discountResult.type,
            percentage:
              discountResult.type === "PERCENTAGE"
                ? discountResult.value
                : undefined,
            excludeAddOns: discountResult.excludeAddOns,
          });
          base.setDiscountCode(discountResult.code || code);
        } else {
          base.setDiscount(null);
          const errorMsg = discountResult?.message || "Invalid discount code";
          base.setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        base.setDiscount(null);
        const errorMsg =
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : null) ||
          "Failed to validate discount";
        base.setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        base.setIsLoading(false);
      }
    },
    [onValidateDiscount, base, ticketSelections, ticketTypes],
  );

  // Clear discount
  const clearDiscount = useCallback(() => {
    base.setDiscount(null);
    base.setDiscountCode("");
  }, [base]);

  // Validate current step
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "dates":
        return selectedSession !== null;
      case "tickets": {
        const hasTickets = ticketSelections.some((s) => s.quantity > 0);
        if (!hasTickets) return false;
        return constraintErrors.length === 0 && !hasMixedPolicies;
      }
      case "addons":
        return base.addOnGroupsValid;
      case "participants":
        return base.isParticipantsValid;
      case "userform":
        return base.isUserFormValid;
      case "basketChoice":
        return true;
      default:
        return false;
    }
  }, [step, selectedSession, ticketSelections, base, constraintErrors, hasMixedPolicies]);

  // Get booking data for API
  const getBookingData = useCallback(() => {
    const { personalDetails, userFormValues, addOnSelections } = base;
    const [guest, ...additionalGuests] = personalDetails.guests;

    const tickets = ticketSelections
      .map((sel) => {
        const ticketType = (initialTicketTypes ?? []).find(
          (tt: any) => tt._id === sel.ticketTypeId,
        );
        return ticketType ? { ...ticketType, count: sel.quantity } : null;
      })
      .filter(Boolean);

    const allAddOns = [
      ...enabledAddOns,
      ...addOnGroups.flatMap((g: any) => g.addOns),
    ];
    const selectedAddOns = addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = allAddOns.find((a: any) => a.id === sel.addOnId);
        return {
          _id: addOn?.id,
          id: addOn?.id,
          name: addOn?.name,
          price: addOn?.price,
          quantity: sel.quantity,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    const data: any = {
      bookingType: "regular",
      tickets,
      user: {
        _id: user?._id || user?.id,
        profile: {
          firstName: guest?.firstName,
          lastName: guest?.lastName,
          phone: guest?.phoneNumber,
        },
        renderEmail: guest?.email,
      },
      checkoutGuestsInformation: personalDetails.guests,
      marketingOptIn: guest?.marketingOptIn,
      courseId: course._id,
      startDateTime: selectedSession?.startDateTime,
      additionalGuests,
      bundleID: bundleID,
      discountCode: base.discountCode,
      credit: 0,
      sessionid: selectedSession?.id,
      deliveryAddress: isSendingMaterial
        ? personalDetails.deliveryAddress
        : null,
      userInputs: userFormValues,
      participants: showParticipantsStep
        ? participantsToPayload(base.participantsValue)
        : undefined,
      addOns: selectedAddOns,
      timezone: course.timezone,
      _pricingSummary: {
        grossTotal: total + discountAmount,
        netTotal: total,
        discountAmount,
        discountType: bundleID ? "bundle" : base.discount ? "discount" : undefined,
        currency: course.currency || "GBP",
      },
    };

    if (isV2) {
      Object.assign(data, v2.buildBookingData());
    }

    return data;
  }, [
    base,
    bundleID,
    ticketSelections,
    initialTicketTypes,
    enabledAddOns,
    addOnGroups,
    selectedSession,
    course,
    user,
    isSendingMaterial,
    total,
    discountAmount,
    isV2,
    v2.buildBookingData,
  ]);

  return {
    // State
    step,
    sessions,
    selectedSession,
    ticketTypes,
    ticketSelections,
    constraintErrors,
    hasMixedPolicies,
    v2PricingResults: v2.pricingResults,
    v2InstalmentDiscountShares: v2.instalmentDiscountShares,
    isV2,
    addOnSelections: base.addOnSelections,
    userFormValues: base.userFormValues,
    personalDetails: base.personalDetails,
    discount: base.discount,
    discountCode: base.discountCode,
    error: base.error,
    isLoading: base.isLoading,
    isFullyLoaded,
    isConfirmingFreeBooking: base.isConfirmingFreeBooking,
    isUserFormValid: base.isUserFormValid,
    participantsValue: base.participantsValue,
    isParticipantsValid: base.isParticipantsValid,
    buyerAttendeeSeededRef: base.buyerAttendeeSeededRef,
    showParticipantsStep,

    // Computed
    total,
    totalRemainingBalance,
    discountAmount,
    isFree,
    remainingAvailability,
    visibleSteps,
    totalGuestCount,
    ticketSeatGroups,
    collectSystemGuests,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    enabledAddOns,
    addOnGroups,
    addOnGroupsValid: base.addOnGroupsValid,
    addOnsTotal: base.addOnsTotal,
    course,
    bundleID,

    // Actions
    goToNextStep,
    goToPreviousStep,
    goToStep,
    selectSession,
    updateTicketSelection,
    updateAddOnSelection: base.updateAddOnSelection,
    updateUserFormValue: base.updateUserFormValue,
    setUserFormValues: base.setUserFormValues,
    setIsUserFormValid: base.setIsUserFormValid,
    setParticipantsValue: base.setParticipantsValue,
    setIsParticipantsValid: base.setIsParticipantsValid,
    updatePersonalDetails: base.updatePersonalDetails,
    updateDeliveryAddress: base.updateDeliveryAddress,
    loadMoreDates,
    applyDiscount,
    clearDiscount,
    canContinue,
    getBookingData,
    setIsConfirmingFreeBooking: base.setIsConfirmingFreeBooking,
    clearError: base.clearError,
  };
}
